import { useState, useEffect } from 'react';
import { LockIcon } from '../components/Icons';
import { GlassCard } from '../components/GlassCard';
import { Toast } from '../components/Toast';
import { api, ApiError } from '../lib/api';
import { ContactCardSkeleton } from '../components/Skeleton';
import { T } from '../lib/design-tokens';
import type { VaultContactRecord } from '../types';

function maskEmail(email: string) {
  const [local, domain] = email.split('@');
  if (!domain || local == null) return email;
  const masked = local.length > 2 ? local[0] + '***' + local[local.length - 1] : '***';
  return `${masked}@${domain}`;
}

function maskPhone(phone: string) {
  const digits = phone.replace(/\D/g, '');
  if (digits.length >= 4) return '****' + digits.slice(-4);
  return '****';
}

function formatLastUsed(iso: string | null) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  if (days < 1) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
}

const E164_RE = /^\+[1-9]\d{1,14}$/;

export function VaultPage() {
  const [contacts, setContacts] = useState<VaultContactRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api<VaultContactRecord[]>('/contacts')
      .then(setContacts)
      .catch(() => setToast('Failed to load contacts'))
      .finally(() => setLoading(false));
  }, []);

  const resetForm = () => {
    setName('');
    setPhone('');
    setEmail('');
    setCompany('');
    setErrors({});
    setShowForm(false);
  };

  const handleCreate = async () => {
    const errs: Record<string, string> = {};
    if (!name.trim() || name.trim().length > 200) errs.name = 'Name is required (1-200 chars)';
    if (phone.trim() && !E164_RE.test(phone.trim())) errs.phone = 'Must be E.164 format (e.g. +14155551234)';
    if (phone.trim() && phone.trim().length > 50) errs.phone = 'Max 50 characters';
    if (email.trim() && !email.includes('@')) errs.email = 'Invalid email address';
    if (email.trim() && email.trim().length > 200) errs.email = 'Max 200 characters';
    if (company.trim().length > 200) errs.company = 'Max 200 characters';

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setSaving(true);
    try {
      const body: Record<string, string> = { name: name.trim() };
      if (phone.trim()) body.phone = phone.trim();
      if (email.trim()) body.email = email.trim();
      if (company.trim()) body.company = company.trim();

      const contact = await api<VaultContactRecord>('/contacts', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setContacts((prev) => [contact, ...prev]);
      setToast('Contact saved.');
      resetForm();
    } catch (err) {
      if (err instanceof ApiError && err.details?.errors) {
        const fieldErrors: Record<string, string> = {};
        for (const [field, msg] of Object.entries(err.details.errors as Record<string, string>)) {
          fieldErrors[field] = msg;
        }
        setErrors(fieldErrors);
      } else {
        setToast(err instanceof ApiError ? err.message : 'Failed to save contact');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api(`/contacts/${id}`, { method: 'DELETE' });
      setContacts((prev) => prev.filter((c) => c.id !== id));
      setToast('Contact deleted.');
    } catch (err) {
      setToast(err instanceof ApiError ? err.message : 'Failed to delete contact');
    } finally {
      setDeleting(null);
    }
  };

  const inputStyle = (hasError: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '10px 14px',
    borderRadius: 10,
    border: hasError ? `1.5px solid ${T.red}` : `1.5px solid ${T.glassBorder}`,
    fontFamily: T.sans,
    fontSize: 14,
    color: T.cream,
    outline: 'none',
    background: T.surface,
    transition: 'border-color 0.2s',
  });

  return (
    <div className="max-w-[760px] mx-auto" style={{ padding: '40px 40px' }}>
      <div className="flex justify-between items-center mb-2.5" style={{ animation: 'fadeUp 0.5s ease both' }}>
        <h1
          style={{
            fontFamily: T.serif,
            fontSize: 30,
            fontWeight: 400,
            color: T.cream,
            letterSpacing: '-0.5px',
          }}
        >
          Vault
        </h1>
        <button
          type="button"
          onClick={() => setShowForm(true)}
          style={{
            padding: '9px 20px',
            borderRadius: 10,
            border: 'none',
            background: T.gold,
            color: T.bg,
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: T.mono,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.boxShadow = `0 0 20px ${T.goldGlow}`)}
          onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
        >
          Add Credential
        </button>
      </div>
      <p
        style={{
          fontFamily: T.mono,
          fontSize: 11,
          color: T.cream20,
          marginBottom: 28,
          letterSpacing: '0.05em',
        }}
      >
        ENCRYPTED. EXPOSED ONLY PER-OPERATION, JUST-IN-TIME.
      </p>

      {showForm && (
        <GlassCard
          style={{
            padding: '24px 28px',
            marginBottom: 24,
            border: `1.5px solid ${T.goldDim}`,
            background: T.goldGlow,
            animation: 'fadeUp 0.3s ease both',
          }}
        >
          <h3
            style={{
              fontFamily: T.mono,
              fontSize: 10,
              fontWeight: 600,
              color: T.cream20,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              marginBottom: 16,
            }}
          >
            New Contact
          </h3>

          <div className="flex flex-col gap-3.5">
            {['name', 'phone', 'email', 'company'].map((field) => (
              <div key={field}>
                <label
                  className="block mb-1 text-xs font-medium"
                  style={{ color: T.cream60, fontFamily: T.sans }}
                >
                  {field === 'name' ? 'Name *' : field.charAt(0).toUpperCase() + field.slice(1)}
                </label>
                <input
                  value={
                    field === 'name'
                      ? name
                      : field === 'phone'
                        ? phone
                        : field === 'email'
                          ? email
                          : company
                  }
                  onChange={(e) => {
                    if (field === 'name') setName(e.target.value);
                    else if (field === 'phone') setPhone(e.target.value);
                    else if (field === 'email') setEmail(e.target.value);
                    else setCompany(e.target.value);
                  }}
                  placeholder={
                    field === 'name'
                      ? 'Contact name'
                      : field === 'phone'
                        ? '+14155551234'
                        : field === 'email'
                          ? 'contact@example.com'
                          : 'Company name'
                  }
                  type={field === 'email' ? 'email' : 'text'}
                  style={inputStyle(!!errors[field])}
                  onFocus={(e) => (e.currentTarget.style.borderColor = T.gold)}
                  onBlur={(e) =>
                    (e.currentTarget.style.borderColor = errors[field] ? T.red : T.glassBorder)
                  }
                />
                {errors[field] && (
                  <div className="text-xs mt-1" style={{ color: T.red, fontFamily: T.sans }}>
                    {errors[field]}
                  </div>
                )}
              </div>
            ))}

            <div className="flex items-center gap-3 mt-1">
              <button
                type="button"
                onClick={handleCreate}
                disabled={saving}
                style={{
                  padding: '9px 20px',
                  borderRadius: 10,
                  border: 'none',
                  background: saving ? T.cream20 : T.gold,
                  color: T.bg,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: saving ? 'default' : 'pointer',
                  fontFamily: T.sans,
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                style={{
                  padding: '9px 20px',
                  borderRadius: 10,
                  border: `1px solid ${T.cream12}`,
                  background: 'transparent',
                  color: T.cream60,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontFamily: T.sans,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </GlassCard>
      )}

      {loading && (
        <div className="flex flex-col gap-2">
          {[0, 1, 2].map((i) => (
            <ContactCardSkeleton key={i} />
          ))}
        </div>
      )}

      {!loading && contacts.length === 0 && !showForm && (
        <GlassCard style={{ padding: 64, textAlign: 'center' }}>
          <div style={{ fontFamily: T.sans, fontSize: 14, fontWeight: 500, color: T.cream35, marginBottom: 8 }}>
            No credentials saved yet.
          </div>
          <div style={{ fontFamily: T.sans, fontSize: 12, color: T.cream20, marginBottom: 16 }}>
            Add credentials so we can use them when you need to call or email.
          </div>
          <button
            type="button"
            onClick={() => setShowForm(true)}
            style={{
              padding: '9px 20px',
              borderRadius: 10,
              border: 'none',
              background: T.gold,
              color: T.bg,
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: T.mono,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            Add Credential
          </button>
        </GlassCard>
      )}

      {!loading && contacts.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {contacts.map((contact, i) => (
            <GlassCard
              key={contact.id}
              hover
              style={{
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                animation: `fadeUp 0.4s ease ${i * 0.06}s both`,
              }}
            >
              <div
                className="shrink-0 flex items-center justify-center rounded-[10px]"
                style={{
                  width: 38,
                  height: 38,
                  background: T.cream04,
                  color: T.cream20,
                }}
              >
                <LockIcon size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div style={{ fontFamily: T.sans, fontSize: 14, fontWeight: 500, color: T.cream }}>
                  {contact.company || contact.name}
                </div>
                <div style={{ fontFamily: T.mono, fontSize: 11, color: T.cream12 }}>
                  {contact.email ? maskEmail(contact.email) : contact.phone ? maskPhone(contact.phone) : '—'}
                </div>
              </div>
              <span style={{ fontFamily: T.mono, fontSize: 10, color: T.cream12 }}>
                {formatLastUsed(contact.lastUsed) || formatLastUsed(contact.updatedAt)}
              </span>
              {deleting === contact.id ? (
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs" style={{ color: T.red, fontFamily: T.sans }}>Delete?</span>
                  <button
                    type="button"
                    onClick={() => handleDelete(contact.id)}
                    className="border-none bg-transparent cursor-pointer p-0 text-xs font-semibold"
                    style={{ color: T.red }}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleting(null)}
                    className="border-none bg-transparent cursor-pointer p-0 text-xs font-medium"
                    style={{ color: T.cream35 }}
                  >
                    No
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setDeleting(contact.id)}
                  className="border-none bg-transparent cursor-pointer p-0 text-xs font-medium shrink-0 opacity-70 hover:opacity-100 transition-opacity"
                  style={{ color: T.red }}
                >
                  Delete
                </button>
              )}
            </GlassCard>
          ))}
        </div>
      )}

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
